/**
 * Shared chip-building helpers for renderers that turn `chipsFromAnswers`
 * keys into chip strings (`validation_screen`, `character_reveal_gate`,
 * and any future renderers with the same UX).
 *
 * Keeping a single source of truth here is what prevents silent
 * divergence between the screens — e.g. one accepting numeric/boolean
 * answers via `String(raw)` while another silently drops them. A funnel
 * author should be able to reuse the same `chipsFromAnswers` keys
 * across step types and trust the chips will render identically.
 */

/**
 * Coerce a single raw answer value into a chip string.
 *
 * Rules:
 *   - non-empty string  → return as-is
 *   - number or boolean → coerce via `String(raw)` (e.g. `7`, `true`)
 *   - anything else (null, undefined, object, array, empty string) → drop
 */
export const resolveChip = (raw: unknown): string | undefined => {
  if (typeof raw === "string" && raw.trim().length > 0) return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  return undefined;
};

/**
 * Build the final chips array a renderer hands to its presentational
 * component. Honors `data.chips` (literal override) first; otherwise
 * resolves each `chipsFromAnswers` key through `resolveChip`. Returns
 * `undefined` when there are no usable chips so the component can omit
 * the chip row entirely instead of rendering an empty container.
 */
export const buildChipsFromAnswers = (
  data: { chips?: ReadonlyArray<string>; chipsFromAnswers?: ReadonlyArray<string> },
  answers: Record<string, unknown>,
): string[] | undefined => {
  if (data.chips && data.chips.length > 0) return [...data.chips];
  if (!data.chipsFromAnswers || data.chipsFromAnswers.length === 0) return undefined;
  const resolved = data.chipsFromAnswers
    .map((key) => resolveChip(answers[key]))
    .filter((value): value is string => value !== undefined);
  return resolved.length > 0 ? resolved : undefined;
};
